import { useT } from '../../i18n/index.js';
import type { MatchConfigOverrides } from '../types/MatchConfigOverrides.js';
import {
  updateAI,
  updateCombat,
  updateEconomy,
  updateEspionage,
  updateGameSpeed,
  updateSacrifice,
  updateSiege,
  updateVictory,
} from './matchSettingsUpdaters.js';
import { SliderRow, ToggleRow } from './shared.js';

interface Props {
  config: MatchConfigOverrides;
  onChange: (partial: Partial<MatchConfigOverrides>) => void;
}

export function MatchSettingsTab({ config, onChange }: Props) {
  const t = useT();
  return (
    <div className="space-y-6">
      <section>
        <h4 className="text-sm font-medium text-foreground mb-3">{t('match.victory')}</h4>
        <ToggleRow
          label={t('match.tradeRoutes')}
          description={t('match.tradeRoutesHint')}
          value={config.tradeRoutesEnabled ?? true}
          onChange={(v) => onChange({ tradeRoutesEnabled: v })}
        />
        <ToggleRow
          label={t('match.formations')}
          description={t('match.formationsHint')}
          value={config.formationsEnabled ?? true}
          onChange={(v) => onChange({ formationsEnabled: v })}
        />
        <SliderRow
          label={t('match.minEmpireTick')}
          description={t('match.minEmpireTickHint')}
          min={10}
          max={100}
          step={5}
          value={config.victory?.minEmpireTick ?? 35}
          onChange={(v) => onChange(updateVictory(config, { minEmpireTick: v }))}
        />
        <SliderRow
          label={t('match.sustainTicks')}
          description={t('match.sustainTicksHint')}
          min={1}
          max={20}
          step={1}
          value={config.victory?.sustainEmpireTicks ?? 5}
          onChange={(v) => onChange(updateVictory(config, { sustainEmpireTicks: v }))}
        />
      </section>

      <section>
        <h4 className="text-sm font-medium text-foreground mb-3">{t('match.ai')}</h4>
        <SliderRow
          label={t('match.shuffleInterval')}
          description={t('match.shuffleIntervalHint')}
          min={0}
          max={200}
          step={10}
          value={config.ai?.shuffleInterval ?? 0}
          onChange={(v) => onChange(updateAI(config, { shuffleInterval: v }))}
        />
        <SliderRow
          label={t('match.aggressionMin')}
          min={0}
          max={100}
          step={5}
          value={Math.round((config.ai?.aggressionRange?.[0] ?? 0.1) * 100)}
          onChange={(v) =>
            onChange(
              updateAI(config, {
                aggressionRange: [v / 100, config.ai?.aggressionRange?.[1] ?? 0.9],
              }),
            )
          }
        />
      </section>

      <section>
        <h4 className="text-sm font-medium text-foreground mb-3">{t('match.gameSpeed')}</h4>
        <SliderRow
          label={t('match.incomeInterval')}
          description={t('match.incomeIntervalHint')}
          min={5}
          max={30}
          step={5}
          value={config.gameSpeed?.incomeInterval ?? 10}
          onChange={(v) => onChange(updateGameSpeed(config, { incomeInterval: v }))}
        />
        <SliderRow
          label={t('match.empireInterval')}
          description={t('match.empireIntervalHint')}
          min={5}
          max={30}
          step={5}
          value={config.gameSpeed?.empireInterval ?? 10}
          onChange={(v) => onChange(updateGameSpeed(config, { empireInterval: v }))}
        />
        <SliderRow
          label={t('match.movementReset')}
          description={t('match.movementResetHint')}
          min={5}
          max={30}
          step={5}
          value={config.gameSpeed?.movementResetInterval ?? 10}
          onChange={(v) => onChange(updateGameSpeed(config, { movementResetInterval: v }))}
        />
        <SliderRow
          label={t('match.slowIncome')}
          description={t('match.slowIncomeHint')}
          min={10}
          max={60}
          step={10}
          value={config.gameSpeed?.slowIncomeInterval ?? 20}
          onChange={(v) => onChange(updateGameSpeed(config, { slowIncomeInterval: v }))}
        />
        <SliderRow
          label={t('match.aiMove')}
          description={t('match.aiMoveHint')}
          min={1}
          max={20}
          step={1}
          value={config.gameSpeed?.aiMoveInterval ?? 5}
          onChange={(v) => onChange(updateGameSpeed(config, { aiMoveInterval: v }))}
        />
      </section>

      <section>
        <h4 className="text-sm font-medium text-foreground mb-3">{t('match.siege')}</h4>
        <SliderRow
          label={t('match.evalInterval')}
          description={t('match.evalIntervalHint')}
          min={5}
          max={30}
          step={5}
          value={config.siege?.evalInterval ?? 10}
          onChange={(v) => onChange(updateSiege(config, { evalInterval: v }))}
        />
        <SliderRow
          label={t('match.moodPenalty')}
          description={t('match.siegeMoodPenaltyHint')}
          min={0}
          max={20}
          step={1}
          value={config.siege?.moodPenalty ?? 2}
          onChange={(v) => onChange(updateSiege(config, { moodPenalty: v }))}
        />
        <SliderRow
          label={t('match.surrenderMood')}
          description={t('match.surrenderMoodHint')}
          min={0}
          max={50}
          step={5}
          value={config.siege?.surrenderMoodThreshold ?? 10}
          onChange={(v) => onChange(updateSiege(config, { surrenderMoodThreshold: v }))}
        />
        <SliderRow
          label={t('match.surrenderLoyalty')}
          description={t('match.surrenderLoyaltyHint')}
          min={0}
          max={50}
          step={5}
          value={config.siege?.surrenderLoyaltyThreshold ?? 10}
          onChange={(v) => onChange(updateSiege(config, { surrenderLoyaltyThreshold: v }))}
        />
        <SliderRow
          label={t('match.minSiegeTicks')}
          description={t('match.minSiegeTicksHint')}
          min={10}
          max={100}
          step={10}
          value={config.siege?.surrenderMinSiegeTicks ?? 30}
          onChange={(v) => onChange(updateSiege(config, { surrenderMinSiegeTicks: v }))}
        />
        <SliderRow
          label={t('match.capitalMood')}
          description={t('match.capitalMoodHint')}
          min={0}
          max={50}
          step={5}
          value={config.siege?.surrenderCapitalMoodThreshold ?? 5}
          onChange={(v) => onChange(updateSiege(config, { surrenderCapitalMoodThreshold: v }))}
        />
        <SliderRow
          label={t('match.capitalMinTicks')}
          description={t('match.capitalMinTicksHint')}
          min={20}
          max={120}
          step={10}
          value={config.siege?.surrenderCapitalMinSiegeTicks ?? 60}
          onChange={(v) => onChange(updateSiege(config, { surrenderCapitalMinSiegeTicks: v }))}
        />
      </section>

      <section>
        <h4 className="text-sm font-medium text-foreground mb-3">{t('match.espionage')}</h4>
        <SliderRow
          label={t('match.intelDepth1')}
          description={t('match.intelDepth1Hint')}
          min={1}
          max={20}
          step={1}
          value={config.espionage?.intelDeliveryDepth1 ?? 3}
          onChange={(v) => onChange(updateEspionage(config, { intelDeliveryDepth1: v }))}
        />
        <SliderRow
          label={t('match.intelDepth2')}
          description={t('match.intelDepth2Hint')}
          min={1}
          max={20}
          step={1}
          value={config.espionage?.intelDeliveryDepth2 ?? 5}
          onChange={(v) => onChange(updateEspionage(config, { intelDeliveryDepth2: v }))}
        />
        <SliderRow
          label={t('match.detectAdjacent')}
          description={t('match.detectAdjacentHint')}
          min={0}
          max={100}
          step={5}
          value={config.espionage?.detectionChanceAdjacent ?? 30}
          onChange={(v) => onChange(updateEspionage(config, { detectionChanceAdjacent: v }))}
        />
        <SliderRow
          label={t('match.detect2Hex')}
          description={t('match.detect2HexHint')}
          min={0}
          max={100}
          step={5}
          value={config.espionage?.detectionChance2Hex ?? 20}
          onChange={(v) => onChange(updateEspionage(config, { detectionChance2Hex: v }))}
        />
        <SliderRow
          label={t('match.bodyguard')}
          description={t('match.bodyguardHint')}
          min={0}
          max={100}
          step={5}
          value={config.espionage?.bodyguardBlockChance ?? 40}
          onChange={(v) => onChange(updateEspionage(config, { bodyguardBlockChance: v }))}
        />
      </section>

      <section>
        <h4 className="text-sm font-medium text-foreground mb-3">{t('match.sacrifice')}</h4>
        <SliderRow
          label={t('match.minPopulation')}
          description={t('match.minPopulationHint')}
          min={1000}
          max={20000}
          step={500}
          value={config.sacrifice?.minPopulation ?? 5000}
          onChange={(v) => onChange(updateSacrifice(config, { minPopulation: v }))}
        />
        <SliderRow
          label={t('match.faithReturn')}
          description={t('match.faithReturnHint')}
          min={1}
          max={10}
          step={1}
          value={config.sacrifice?.faithReturnNumerator ?? 1}
          onChange={(v) => onChange(updateSacrifice(config, { faithReturnNumerator: v }))}
        />
        <SliderRow
          label={t('match.moodPenalty')}
          description={t('match.sacrificeMoodPenaltyHint')}
          min={-50}
          max={0}
          step={5}
          value={config.sacrifice?.moodPenalty ?? -15}
          onChange={(v) => onChange(updateSacrifice(config, { moodPenalty: v }))}
        />
        <SliderRow
          label={t('match.loyaltyPenalty')}
          description={t('match.loyaltyPenaltyHint')}
          min={-50}
          max={0}
          step={5}
          value={config.sacrifice?.loyaltyPenalty ?? -10}
          onChange={(v) => onChange(updateSacrifice(config, { loyaltyPenalty: v }))}
        />
      </section>

      <section>
        <h4 className="text-sm font-medium text-foreground mb-3">{t('match.combat')}</h4>
        <SliderRow
          label={t('match.surrenderHp')}
          description={t('match.surrenderHpHint')}
          min={5}
          max={50}
          step={5}
          value={config.combat?.surrenderHpThresholdPct ?? 20}
          onChange={(v) => onChange(updateCombat(config, { surrenderHpThresholdPct: v }))}
        />
        <SliderRow
          label={t('match.conversionCost')}
          description={t('match.conversionCostHint')}
          min={10}
          max={50}
          step={5}
          value={config.combat?.surrenderConversionCostPct ?? 25}
          onChange={(v) => onChange(updateCombat(config, { surrenderConversionCostPct: v }))}
        />
      </section>

      <section>
        <h4 className="text-sm font-medium text-foreground mb-3">{t('match.economy')}</h4>
        <SliderRow
          label={t('match.baseFood')}
          description={t('match.baseFoodHint')}
          min={5}
          max={30}
          step={5}
          value={config.economy?.baseFoodPerCity ?? 10}
          onChange={(v) => onChange(updateEconomy(config, { baseFoodPerCity: v }))}
        />
        <SliderRow
          label={t('match.lootGold')}
          description={t('match.lootGoldHint')}
          min={5}
          max={50}
          step={5}
          value={config.economy?.cityLootGoldPerPop ?? 10}
          onChange={(v) => onChange(updateEconomy(config, { cityLootGoldPerPop: v }))}
        />
        <SliderRow
          label={t('match.lootIndustry')}
          description={t('match.lootIndustryHint')}
          min={1}
          max={20}
          step={1}
          value={config.economy?.cityLootIndustryPerTier ?? 5}
          onChange={(v) => onChange(updateEconomy(config, { cityLootIndustryPerTier: v }))}
        />
      </section>
    </div>
  );
}
